import {
  DocumentData,
  DocumentSnapshot,
  QueryDocumentSnapshot,
  QuerySnapshot,
  Timestamp,
} from "firebase/firestore";
import { AnalysisDocument, FavoritDocument, GraphDocument } from "~/src/type/firebase/firestore";
import {
  GraphInfo,
  HeadingAnalysisArticle,
  HeadingFavoritArticle,
} from "~/src/type/ui/article/article";
import { GRAPH_KIND } from "~/src/type/ui/graph/graph";

//------------
// 初期データ
//------------
// 分析記事
export const DEFAULT_DOC_ANALYSIS: AnalysisDocument = {
  userId: "",
  userName: "",
  favoritCount: 0,
  title: "",
  createdAt: undefined, // firestoreに保存するタイミングでserverTimestamp()を設定する
  updatedAt: undefined, // firestoreに保存するタイミングでserverTimestamp()を設定する
  draftUpdatedAt: undefined, // firestoreに保存するタイミングでserverTimestamp()を設定する
  tag: [""],
  works: [""],
  details: "",
  premise: "",
  graphDescription: "",
  conclusion: "",
  reference: [""],
  permissionUser: false,
  permissionAdmin: false,
};

// 分析記事のグラフデータ
export const DEFAULT_DOC_GRAPHINFO: GraphDocument = {
  kind: GRAPH_KIND.NONE,
  xAsixLabel: "",
  yAsixLabel: "",
  zAsixLabel: "",
  graphNameList: [],
  data: {},
  option: {},
  index: undefined,
};

/**
 * 初期分析記事データ生成
 */
export function createEmptyAnalysisDocument(): AnalysisDocument {
  const defaultAnalysis = structuredClone(DEFAULT_DOC_ANALYSIS);
  return defaultAnalysis;
}

/**
 * 分析記事リストデータの空データ除外処理
 */
export function removeEmptyItemFromList(article: AnalysisDocument): void {
  // 分析記事内のリストから、空データを除外する
  article.tag = article.tag.filter((item) => item != "");
  article.works = article.works.filter((item) => item != "");
  if (article.reference) {
    article.reference = article.reference.filter((item) => item != "");
  }

  //------------
  // ただし、必ず1つはデータを用意する
  // 入力フォームを最低1つは配置するため
  //------------
  if (article.tag.length == 0) {
    article.tag.push("");
  }
  if (article.works.length == 0) {
    article.works.push("");
  }
  if (article.reference && article.reference.length == 0) {
    article.reference.push("");
  }
}

/**
 * ドキュメント-グラフデータ構造へのパース処理
 */
export function parseFirestoreToGraphDocument(
  doc: QueryDocumentSnapshot<DocumentData, DocumentData>
): GraphDocument {
  const graphData = doc.data();

  // console.log("parseDocGraphInfo id=" + id);

  return {
    kind: graphData.kind,
    xAsixLabel: graphData.xAsixLabel,
    yAsixLabel: graphData.yAsixLabel,
    zAsixLabel: graphData.zAsixLabel,
    graphNameList: graphData.graphNameList,
    data: graphData.data,
    option: graphData.option,
    index: graphData.index, // undefinedの場合もそのまま
  };
}

/**
 * 分析記事データリストパース
 *   変換元：analysisコレクションデータ
 *   変換先：HeadingAnalysisArticle
 */
export async function parseFirestoreToAnalysisDocumentList(
  querySnapshot: QuerySnapshot<DocumentData, DocumentData>
): Promise<HeadingAnalysisArticle[]> {
  return await Promise.all(
    // ドキュメントを1件ずつパース
    querySnapshot.docs.map(async (analysisDoc) => {
      // 分析記事のデータ構造にパース
      const analysisContents = parseFirestoreToAnalysisDocument(analysisDoc);

      //------------
      // 分析記事データ構造
      //------------
      return {
        id: analysisDoc.id,
        contents: analysisContents,
      };
    })
  );
}

/**
 * 分析記事データリストパース
 *   変換元：favoritコレクションデータ
 *   変換先：HeadingFavoritArticle
 */
export async function parseFavoritFirestoreToAnalysisDocumentList(
  querySnapshot: QuerySnapshot<DocumentData, DocumentData>
): Promise<HeadingFavoritArticle[]> {
  return await Promise.all(
    // ドキュメントを1件ずつパース
    querySnapshot.docs.map(async (favoritDoc) => {
      //------------
      // パース
      //------------
      // いいねされた分析記事ID
      const favoritArticleId = parseFirestoreToFavoritArticleId(favoritDoc);
      // 分析記事のデータ構造にパース
      const analysisContents = parseFirestoreToAnalysisDocument(favoritDoc);

      //------------
      // 分析記事データ構造
      //------------
      return {
        id: favoritArticleId, // いいねされた分析記事ID
        contents: analysisContents, // いいね記録
      };
    })
  );
}

/**
 * ドキュメント - 分析記事データ構造へのパース処理
 */
export function parseFirestoreToAnalysisDocument(
  doc: DocumentSnapshot<DocumentData, DocumentData>
): AnalysisDocument {
  const analysisData = doc.data()! as AnalysisDocument; // todo 強制でOK？

  // 分析記事データ構造へパース
  return {
    userId: analysisData.userId,
    userName: analysisData.userName,
    favoritCount: analysisData.favoritCount,
    title: analysisData.title,
    createdAt: analysisData.createdAt as Timestamp,
    updatedAt: analysisData.updatedAt as Timestamp | undefined,
    draftUpdatedAt: analysisData.draftUpdatedAt as Timestamp | undefined,
    tag: analysisData.tag,
    works: analysisData.works,
    details: analysisData.details,
    premise: analysisData.premise,
    graphDescription: analysisData.graphDescription,
    conclusion: analysisData.conclusion,
    reference: analysisData.reference,
    body: analysisData.body,
    permissionUser: analysisData.permissionUser,
    permissionAdmin: analysisData.permissionAdmin,
  };
}

/**
 * ドキュメント - 分析記事データ構造へのパース処理
 */
export function parseFirestoreToFavoritArticleId(
  doc: DocumentSnapshot<DocumentData, DocumentData>
): string {
  const analysisData = doc.data()! as FavoritDocument; // todo 強制でOK？

  // いいねドキュメント上の「いいねされた分析記事ID」を返す
  return analysisData.analysisId;
}

/**
 * グラフリストをindex順にソートする
 * indexがundefinedのものは元の順序を維持（末尾に配置）
 */
export function sortGraphInfoListByIndex(graphInfoList: GraphInfo[]): GraphInfo[] {
  return [...graphInfoList].sort((a, b) => {
    const indexA = a.info.index;
    const indexB = b.info.index;

    // 両方undefinedの場合は元の順序を維持
    if (indexA === undefined && indexB === undefined) return 0;
    // 片方だけundefinedの場合、undefinedを後ろに
    if (indexA === undefined) return 1;
    if (indexB === undefined) return -1;
    // 両方numberの場合は数値比較
    return indexA - indexB;
  });
}
